import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface TierRevenue {
  tierName: string;
  ticketCount: number;
  totalRevenue: number;
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '1rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.6rem 0.75rem',
    borderBottom: '2px solid #e0e0e0',
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#555',
    textTransform: 'uppercase' as const,
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.9rem',
    verticalAlign: 'middle' as const,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  empty: {
    color: '#666',
    fontStyle: 'italic' as const,
    padding: '1rem 0',
  },
};

export function RevenueByTier() {
  const [revenue, setRevenue] = useState<TierRevenue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function fetchRevenue() {
    try {
      const data = await apiClient<TierRevenue[]>('/legacy-ball/revenue/by-tier');
      setRevenue(data);
      setError('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load revenue data.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchRevenue();
  }, []);

  function formatCurrency(amount: number): string {
    return `$${amount.toFixed(2)}`;
  }

  if (loading) {
    return (
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Revenue by Tier</h2>
        <p style={{ color: '#666' }}>Loading revenue data...</p>
      </div>
    );
  }

  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>Revenue by Tier</h2>
      {error && <div style={styles.error}>{error}</div>}

      {revenue.length === 0 ? (
        <p style={styles.empty}>No revenue data available.</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Tier Name</th>
              <th style={styles.th}>Tickets Sold</th>
              <th style={styles.th}>Total Revenue</th>
            </tr>
          </thead>
          <tbody>
            {revenue.map((entry) => (
              <tr key={entry.tierName}>
                <td style={styles.td}>
                  <strong>{entry.tierName}</strong>
                </td>
                <td style={styles.td}>{entry.ticketCount}</td>
                <td style={styles.td}>{formatCurrency(entry.totalRevenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
